import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { MapPin, Phone, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OFFICES = [
  { title: "Head Office", address: "No 1 Alasia Street, Ikorodu/Shagamu Express Road, Ogijo, Lagos, Nigeria" },
  { title: "Branch Office", address: "Paradise Jetty Block, Walter Carrington Crescent, Victoria Island, Lagos, Nigeria" },
  { title: "Ibadan Office", address: "No 1 Health Centre Street, Felele, Ibadan, Nigeria" },
];

const PHONES = ["+234 706 379 2622", "+234 810 777 8448", "+234 812 480 1607"];

const EASE = [0.16, 1, 0.3, 1];

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Please fill in your name, phone and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received!", { description: "Our team will get back to you shortly." });
      setForm({ name: "", phone: "", message: "" });
    } catch {
      toast.error("Could not send your message. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#0B132B]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="max-w-2xl"
        >
          <p className="text-amber-500 font-display font-semibold tracking-[0.35em] uppercase text-xs mb-4" data-testid="contact-eyebrow">Get In Touch</p>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl lg:text-5xl text-white leading-tight" data-testid="contact-heading">
            Our Offices &amp; Hotlines
          </h2>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-2 gap-10">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: EASE }}
            className="space-y-6"
          >
            <div className="grid gap-6" data-testid="offices-list">
              {OFFICES.map(({ title, address }) => (
                <div key={title} className="flex gap-4 bg-[#1C2541]/60 border border-slate-700/50 rounded-2xl p-6 hover:border-amber-600/50 transition-colors duration-300" data-testid={`office-${title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="w-11 h-11 rounded-xl bg-amber-600/15 flex items-center justify-center shrink-0">
                    <Building2 size={20} className="text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-white">{title}</h3>
                    <p className="mt-1 text-sm text-slate-400 leading-relaxed flex items-start gap-1.5">
                      <MapPin size={14} className="text-amber-600 mt-0.5 shrink-0" /> {address}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3" data-testid="contact-phones">
              {PHONES.map((p) => (
                <a key={p} href={`tel:${p.replace(/\s+/g, "")}`} data-testid={`phone-link-${p.replace(/[^0-9]/g, "")}`} className="flex items-center gap-2.5 bg-amber-600/15 border border-amber-600/40 rounded-full px-5 py-2.5 text-amber-400 font-semibold text-sm hover:bg-amber-600/25 transition-colors duration-200">
                  <Phone size={16} /> {p}
                </a>
              ))}
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
            onSubmit={submit}
            className="bg-[#1C2541]/70 border border-slate-700/50 rounded-3xl p-7 md:p-10"
            data-testid="contact-form"
          >
            <h3 className="font-display font-bold text-xl text-white mb-6">Send a Quick Enquiry</h3>
            <div className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="contact-name" className="text-slate-300">Name *</Label>
                <Input id="contact-name" data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your full name" className="bg-[#0B132B] border-slate-700 text-slate-100 placeholder:text-slate-600 focus-visible:ring-amber-600" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-phone" className="text-slate-300">Phone *</Label>
                <Input id="contact-phone" data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Your phone number" className="bg-[#0B132B] border-slate-700 text-slate-100 placeholder:text-slate-600 focus-visible:ring-amber-600" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-message" className="text-slate-300">Message *</Label>
                <Textarea id="contact-message" data-testid="contact-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your haulage or supply needs..." className="bg-[#0B132B] border-slate-700 text-slate-100 placeholder:text-slate-600 focus-visible:ring-amber-600 min-h-[110px]" />
              </div>
              <Button type="submit" disabled={submitting} data-testid="contact-submit-btn" className="w-full bg-amber-600 hover:bg-amber-500 text-white rounded-full h-12 font-semibold transition-all duration-200 disabled:opacity-60">
                {submitting ? "Sending..." : "Send Enquiry"}
              </Button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
